import React, { useEffect, useRef, useState } from "react";

/**
 * LawAIScene3D — interactive 3D Law + AI hero illustration.
 *
 * Visual narrative (top to bottom, layered):
 *   1. Background: radial halo + dotted depth grid (parallax slow)
 *   2. Mid: animated AI neural network with traveling pulses between nodes
 *   3. Centerpiece: 3D scales of justice with subtly tilting pans
 *   4. Below scales: Greek/Cyprus temple silhouette (architectural base)
 *   5. Foreground: floating law glyphs (gavel, scroll, gavel, paragraph mark)
 *   6. Soft AI-core glow behind the scales (bordeaux radial)
 *
 * Mouse-tracked parallax: each layer translates at a different rate so the
 * scene feels 3D without the cost of WebGL. Pure SVG + CSS transforms.
 * Respects prefers-reduced-motion.
 */
export default function LawAIScene3D({ lang = "el", variant = "card" }) {
  const wrapperRef = useRef(null);
  const [mouse, setMouse] = useState({ x: 0, y: 0 });
  const reducedMotion = typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

  const isBanner = variant === "banner";

  useEffect(() => {
    if (reducedMotion) return;
    const el = wrapperRef.current;
    if (!el) return;
    let raf = null;
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      const x = (e.clientX - r.left) / r.width - 0.5;   // -0.5..0.5
      const y = (e.clientY - r.top) / r.height - 0.5;
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setMouse({ x, y }));
    };
    const onLeave = () => setMouse({ x: 0, y: 0 });
    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseleave", onLeave);
    return () => {
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseleave", onLeave);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [reducedMotion]);

  // Layer parallax helper
  const px = (depth) => ({
    transform: reducedMotion
      ? "none"
      : `translate3d(${mouse.x * depth * -20}px, ${mouse.y * depth * -16}px, 0)`,
    transition: "transform .25s cubic-bezier(0.16, 1, 0.3, 1)",
  });

  // 8 neural nodes positioned around the scales (relative 0..100 coordinates)
  const nodes = [
    { id: "n1", x: 15, y: 22, r: 5  },
    { id: "n2", x: 82, y: 18, r: 4  },
    { id: "n3", x: 88, y: 50, r: 6  },
    { id: "n4", x: 78, y: 78, r: 4  },
    { id: "n5", x: 50, y: 88, r: 5  },
    { id: "n6", x: 22, y: 78, r: 4  },
    { id: "n7", x: 10, y: 52, r: 6  },
    { id: "n8", x: 50, y: 10, r: 5  },
  ];

  // Links between adjacent nodes (forms ring) + diagonals through centre
  const links = [
    ["n1","n2"], ["n2","n3"], ["n3","n4"], ["n4","n5"],
    ["n5","n6"], ["n6","n7"], ["n7","n1"], ["n8","n5"],
    ["n8","n3"], ["n8","n7"], ["n1","n5"], ["n3","n6"],
  ];
  const nodeMap = Object.fromEntries(nodes.map(n => [n.id, n]));

  return (
    <div
      ref={wrapperRef}
      className="relative aspect-[5/4] sm:aspect-[6/5] w-full overflow-hidden rounded-[32px]"
      style={{ perspective: "1600px" }}
      data-testid="law-ai-scene"
    >
      {/* Layer 0: radial bordeaux/navy halo + soft dotted grid */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(900px circle at 50% 55%, rgba(248,219,228,0.85) 0%, rgba(216,226,220,0.45) 35%, rgba(255,255,255,0.0) 70%), linear-gradient(180deg, #FBFCFE 0%, #F4F6FA 100%)",
        }}
      />
      <svg className="absolute inset-0 w-full h-full opacity-50 pointer-events-none" aria-hidden="true">
        <defs>
          <pattern id="lawai-dots" x="0" y="0" width="22" height="22" patternUnits="userSpaceOnUse">
            <circle cx="2" cy="2" r="1" fill="rgba(10,25,47,0.10)" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#lawai-dots)" />
      </svg>

      {/* Layer 1: AI core glow behind scales */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          ...px(0.35),
          background:
            "radial-gradient(420px circle at 50% 48%, rgba(139,30,63,0.30) 0%, rgba(139,30,63,0.10) 30%, transparent 70%)",
        }}
      />

      {/* Layer 2: Animated neural network */}
      <div className="absolute inset-0" style={px(0.6)}>
        <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full" preserveAspectRatio="none" aria-hidden="true">
          <defs>
            <linearGradient id="link-grad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%"  stopColor="#0A192F" stopOpacity="0" />
              <stop offset="50%" stopColor="#8B1E3F" stopOpacity="0.6" />
              <stop offset="100%" stopColor="#0A192F" stopOpacity="0" />
            </linearGradient>
            <filter id="node-glow">
              <feGaussianBlur stdDeviation="0.6" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Links (lines) */}
          {links.map(([a, b], i) => {
            const A = nodeMap[a], B = nodeMap[b];
            return (
              <g key={`${a}-${b}`}>
                <line x1={A.x} y1={A.y} x2={B.x} y2={B.y} stroke="rgba(10,25,47,0.18)" strokeWidth="0.18" />
                {/* Animated pulse traveling along the line */}
                {!reducedMotion && (
                  <circle r="0.7" fill="#8B1E3F" filter="url(#node-glow)">
                    <animateMotion
                      dur={`${3.2 + (i % 5) * 0.7}s`}
                      repeatCount="indefinite"
                      begin={`${i * 0.45}s`}
                      path={`M ${A.x} ${A.y} L ${B.x} ${B.y}`}
                    />
                    <animate attributeName="opacity" values="0;1;1;0" dur={`${3.2 + (i % 5) * 0.7}s`} repeatCount="indefinite" begin={`${i * 0.45}s`} />
                  </circle>
                )}
              </g>
            );
          })}

          {/* Nodes */}
          {nodes.map((n, i) => (
            <g key={n.id}>
              <circle cx={n.x} cy={n.y} r={n.r / 6} fill="#0A192F" />
              <circle cx={n.x} cy={n.y} r={n.r / 4} fill="none" stroke="#8B1E3F" strokeWidth="0.18" opacity="0.7">
                {!reducedMotion && (
                  <animate attributeName="r" values={`${n.r/4};${n.r/3};${n.r/4}`} dur="3.6s" begin={`${i * 0.3}s`} repeatCount="indefinite" />
                )}
              </circle>
            </g>
          ))}
        </svg>
      </div>

      {/* Layer 3: Greek temple base (silhouette) */}
      <div className="absolute inset-x-0 bottom-0 h-1/3 pointer-events-none" style={px(0.2)}>
        <svg viewBox="0 0 600 200" preserveAspectRatio="xMidYMax meet" className="w-full h-full" aria-hidden="true">
          {/* Pediment */}
          <polygon points="180,40 300,4 420,40" fill="#0A192F" opacity="0.95" />
          {/* Entablature */}
          <rect x="160" y="40" width="280" height="14" fill="#0A192F" opacity="0.95" />
          {/* 6 Doric columns */}
          {[180, 226, 272, 318, 364, 410].map((x, i) => (
            <g key={x}>
              {/* Capital */}
              <rect x={x - 2} y="54" width="18" height="6" fill="#0A192F" />
              {/* Shaft with fluting */}
              <rect x={x} y="60" width="14" height="120" fill="#0F2545" />
              <line x1={x + 3} y1="62" x2={x + 3} y2="178" stroke="#1E3A5F" strokeWidth="0.6" />
              <line x1={x + 7} y1="62" x2={x + 7} y2="178" stroke="#1E3A5F" strokeWidth="0.6" />
              <line x1={x + 11} y1="62" x2={x + 11} y2="178" stroke="#1E3A5F" strokeWidth="0.6" />
              {/* Base */}
              <rect x={x - 2} y="180" width="18" height="6" fill="#0A192F" />
            </g>
          ))}
          {/* Stylobate */}
          <rect x="150" y="186" width="300" height="10" fill="#0A192F" />
          {/* Side shadows */}
          <rect x="0" y="186" width="150" height="10" fill="#0A192F" opacity="0.15" />
          <rect x="450" y="186" width="150" height="10" fill="#0A192F" opacity="0.15" />
        </svg>
      </div>

      {/* Layer 4: Scales of justice (centerpiece) */}
      <div className="absolute inset-0 flex items-center justify-center" style={px(1.0)}>
        <svg viewBox="0 0 360 360" className="w-3/5 h-3/5 drop-shadow-[0_30px_50px_rgba(10,25,47,0.20)] scales-tilt" aria-hidden="true">
          <defs>
            <linearGradient id="scale-metal" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#FBE9EE" />
              <stop offset="55%" stopColor="#8B1E3F" />
              <stop offset="100%" stopColor="#6F1530" />
            </linearGradient>
            <linearGradient id="navy-rod" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#1E3A5F" />
              <stop offset="100%" stopColor="#0A192F" />
            </linearGradient>
            <filter id="metal-shine">
              <feGaussianBlur stdDeviation="2.5" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Center vertical post */}
          <rect x="174" y="60" width="12" height="220" fill="url(#navy-rod)" rx="3" />
          {/* Base block */}
          <rect x="140" y="278" width="80" height="14" fill="#0A192F" rx="3" />
          <rect x="125" y="290" width="110" height="10" fill="#0A192F" rx="3" />

          {/* Top knob */}
          <circle cx="180" cy="58" r="10" fill="#0A192F" />
          <circle cx="180" cy="58" r="5" fill="#8B1E3F" />

          {/* Horizontal beam (slightly tilted via CSS animation on parent) */}
          <g className="scales-beam" style={{ transformOrigin: "180px 78px" }}>
            <rect x="50" y="74" width="260" height="8" fill="url(#navy-rod)" rx="4" />
            {/* Pivot diamond */}
            <polygon points="180,68 188,78 180,88 172,78" fill="#8B1E3F" filter="url(#metal-shine)" />

            {/* Left chain + pan */}
            <line x1="80" y1="82" x2="80" y2="140" stroke="#0A192F" strokeWidth="2" />
            <line x1="80" y1="82" x2="60" y2="140" stroke="#0A192F" strokeWidth="1.5" />
            <line x1="80" y1="82" x2="100" y2="140" stroke="#0A192F" strokeWidth="1.5" />
            <ellipse cx="80" cy="148" rx="46" ry="10" fill="url(#scale-metal)" />
            <path d="M 34 148 Q 80 192 126 148 Z" fill="url(#scale-metal)" opacity="0.95" />
            <ellipse cx="80" cy="148" rx="46" ry="6" fill="#FBE9EE" opacity="0.4" />

            {/* Right chain + pan */}
            <line x1="280" y1="82" x2="280" y2="140" stroke="#0A192F" strokeWidth="2" />
            <line x1="280" y1="82" x2="260" y2="140" stroke="#0A192F" strokeWidth="1.5" />
            <line x1="280" y1="82" x2="300" y2="140" stroke="#0A192F" strokeWidth="1.5" />
            <ellipse cx="280" cy="148" rx="46" ry="10" fill="url(#scale-metal)" />
            <path d="M 234 148 Q 280 192 326 148 Z" fill="url(#scale-metal)" opacity="0.95" />
            <ellipse cx="280" cy="148" rx="46" ry="6" fill="#FBE9EE" opacity="0.4" />
          </g>
        </svg>
      </div>

      {/* Layer 5: Floating law glyphs */}
      <div className="absolute inset-0 pointer-events-none" style={px(1.4)}>
        {/* Dikigoros.io brand shield top-left */}
        <div className="absolute top-[12%] left-[8%] h-12 w-12 rounded-2xl bg-white shadow-[0_12px_30px_rgba(10,25,47,0.10)] border border-slate-200 flex items-center justify-center drift-slow" style={{ animationDelay: "0.6s" }}>
          <img
            src="/dikigoros-icon-64.png"
            alt="Dikigoros.io"
            width={28}
            height={28}
            className="h-7 w-7 object-contain"
          />
        </div>
        {/* Gavel top-right */}
        <div className="absolute top-[10%] right-[6%] h-12 w-12 rounded-2xl bg-white shadow-[0_12px_30px_rgba(10,25,47,0.10)] border border-slate-200 flex items-center justify-center drift-slower">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#0A192F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="m14.5 12.5-3-3M9.5 11.5 3 18l3 3 6.5-6.5M21 9l-9-9-3 3 9 9 3-3zM4 21h16" />
          </svg>
        </div>
        {/* Euro currency bottom-left (Cyprus + EU legal/financial context) */}
        <div className="absolute bottom-[18%] left-[10%] h-11 w-11 rounded-xl bg-white shadow-[0_12px_30px_rgba(10,25,47,0.10)] border border-slate-200 flex items-center justify-center drift-slower" style={{ animationDelay: "1.2s" }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8B1E3F" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M4 10h12M4 14h9M18.5 5A8 8 0 1 0 18.5 19" />
          </svg>
        </div>
        {/* AI sparkle bottom-right */}
        <div className="absolute bottom-[20%] right-[8%] h-11 w-11 rounded-xl bg-white shadow-[0_12px_30px_rgba(10,25,47,0.10)] border border-slate-200 flex items-center justify-center drift-slow" style={{ animationDelay: "0.3s" }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0A192F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M12 3v2M12 19v2M5 12H3M21 12h-2M6.34 6.34 4.93 4.93M19.07 19.07l-1.41-1.41M17.66 6.34l1.41-1.41M4.93 19.07l1.41-1.41" />
            <circle cx="12" cy="12" r="4" fill="#8B1E3F" stroke="#8B1E3F" />
          </svg>
        </div>
      </div>

      {/* Caption pill bottom-centre */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/90 backdrop-blur border border-slate-200 shadow-sm">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
        <span className="text-[11px] font-bold tracking-[0.18em] uppercase text-[#0A192F]">
          {lang === "el" ? "AI · Κυπριακό δίκαιο · ζωντανά" : "AI · Cyprus law · live"}
        </span>
      </div>

      <style>{`
        .scales-tilt {
          animation: ${reducedMotion ? "none" : "tilt-bob 6.5s ease-in-out infinite"};
        }
        @keyframes tilt-bob {
          0%, 100% { transform: translateY(0) rotate(-1.2deg); }
          50%      { transform: translateY(-4px) rotate(1.2deg); }
        }
        .scales-beam {
          animation: ${reducedMotion ? "none" : "beam-tilt 6.5s ease-in-out infinite"};
          transform-box: fill-box;
        }
        @keyframes beam-tilt {
          0%, 100% { transform: rotate(-2.5deg); }
          50%      { transform: rotate(2.5deg); }
        }
      `}</style>
    </div>
  );
}
